/**
 * useModalOpen — RW-2953.
 *
 * The floating buttons (QuickActionsFab, the feed's create "+", the bug
 * launcher) had no way to know a modal was up, so they stayed on top of it:
 * on a phone the two coral "+" buttons and the launcher sat over the
 * composer's Post button. This is the signal they were missing, built the same
 * way as its neighbour useFabClearance.js (a module-level store plus a hook
 * that subscribes to it).
 *
 * Modals register while they are open:
 *   - a component that is mounted the whole time calls
 *     `useModalOpenRegistration(open)`;
 *   - a portal modal that only exists while open renders `<ModalOpenMarker />`
 *     inside itself (or calls `useModalOpenRegistration(true)`).
 *
 * Floating buttons read `useAnyModalOpen()`: QuickActionsFab and the create
 * "+" hide; the bug launcher stays above the modal (it is how a bug inside a
 * modal gets reported) and moves off the modal's buttons instead — see
 * bugLauncherSpot in src/lib/fabStack.js.
 */
import { useEffect, useState } from 'react';

const open = new Set(); // one token per open modal
const listeners = new Set();

const emit = () => { const n = open.size; listeners.forEach((fn) => fn(n)); };

/** How many modals are registered as open right now. */
export const openModalCount = () => open.size;

/** Register as an open modal while `isOpen` is true and the caller is mounted. */
export function useModalOpenRegistration(isOpen = true) {
  useEffect(() => {
    if (!isOpen) return undefined;
    const token = {};
    open.add(token);
    emit();
    return () => {
      if (open.delete(token)) emit();
    };
  }, [isOpen]);
}

/** Drop inside a modal that only renders while it is open. Renders nothing. */
export function ModalOpenMarker() {
  useModalOpenRegistration(true);
  return null;
}

/** How many modals are open, live. A second modal over the first changes it. */
export function useOpenModalCount() {
  const [count, setCount] = useState(openModalCount);
  useEffect(() => {
    listeners.add(setCount);
    setCount(openModalCount());
    return () => { listeners.delete(setCount); };
  }, []);
  return count;
}

/** True while any modal is registered as open. */
export function useAnyModalOpen() {
  return useOpenModalCount() > 0;
}
